import { type FC, useEffect } from "preact/compat";
import { useSignal, useSignalEffect } from "@preact/signals";
import { useNavigate, useSearchParams } from "react-router-dom";
import { isSameDomain } from "~/core/slices/api/utils";
import { me, useApp } from "~/core/store";
import { useBreadcrumbs, useTitle } from "~/hooks";
import ChevronIcon from "@material-design-icons/svg/outlined/chevron_right.svg";
import DeleteIcon from "@material-design-icons/svg/outlined/delete.svg";
import EmblemIcon from "~/assets/engage-logo.svg";
import Loader from "~/assets/loader.svg";
import BottomIcon from "~/assets/marathon-by-kase.svg";
import cn from "classnames";

const Login: FC = () => {
    const {
        api: {
            login,
            loginAs,
            connect,
            connecting,
            waiting,
            waitingAccount,
            connection,
            accounts,
            accountsLoaded,
        },
        share,
    } = useApp();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const installation =
        params.get("installation") !== "_"
            ? (params.get("installation") ?? undefined)
            : undefined;
    const continuePath = params.get("continue") ?? undefined;
    const prompt = params.get("prompt");
    const showAccounts = useSignal(!installation);
    const applyFilter = useSignal(Boolean(installation));
    const error = useSignal(
        params.get("error") === "fail"
            ? "Failed to login into chosen installation. Please try again"
            : null,
    );
    const filteredAccounts = accounts.value.filter(
        (account) =>
            !installation ||
            !applyFilter.value ||
            isSameDomain(account.domain, installation),
    );

    const isNotConnected =
        !connection.value && !connecting.value && !waiting.value;

    useBreadcrumbs([{ name: "Login into App" }]);
    useTitle("Login | Marathon Engage");

    useEffect(() => {
        if (prompt === "force") {
            connecting.value = false;
        } else {
            connect(installation ?? undefined).then(() => {
                if (installation && !connection.peek()) {
                    showAccounts.value = true;
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [installation, prompt]);

    useSignalEffect(() => {
        if (connection.value) {
            const next = continuePath;
            // @TODO: check for only allowed params in order to prevent XSS
            const others = Array.from(params.entries())
                .filter(
                    ([key]) =>
                        key !== "continue" &&
                        key !== "installation" &&
                        key !== "prompt",
                )
                .map(([key, value]) => `${key}=${value}`)
                .join("&");

            navigate(`${next || me("dashboard")}${others ? `?${others}` : ""}`);
        }
    });

    const handleLogin = () => {
        login({
            installation,
            continuePath,
        });
    };

    return (
        <div
            style={{
                backgroundImage: "url('/_assets/login-bg.jpeg')",
                backgroundSize: "cover",
                backgroundPosition: "center",
            }}
            className="relative flex h-full w-full flex-col items-center justify-center text-white"
        >
            {(share.loading.value || share.sharedFiles.value.length > 0) &&
                accountsLoaded.value && (
                    <div className="absolute top-[calc(1rem+var(--safe-top))] left-1/2 -translate-x-1/2 p-4 bg-black/50 rounded-3xl text-sm text-white z-10 text-center backdrop-blur w-[calc(100%-4rem)]">
                        You have shared files with Engage, please login to
                        continue.
                    </div>
                )}
            <div className="mb-12">
                <EmblemIcon className="max-w-full px-4 md:px-0" />
            </div>
            {error.value && (
                <div className="mb-4 text-red-500 text-sm">{error.value}</div>
            )}
            <div className="flex select-none flex-col items-center">
                {isNotConnected && applyFilter.value && (
                    <div className="mb-4 text-center">
                        <p>
                            <span>You're accessing </span>
                            <span className="font-bold text-sky-500">
                                {installation}
                            </span>{" "}
                            installation.
                        </p>
                        <p className="mt-1">
                            <button
                                type="button"
                                className="text-sky-500 hover:text-sky-600 focus:text-sky-600"
                                onClick={() => (applyFilter.value = false)}
                            >
                                Click to choose from all your accounts
                            </button>
                        </p>
                    </div>
                )}
                {showAccounts.value && !accountsLoaded.value && (
                    <div className="flex items-center gap-x-2">
                        <Loader width="16" height="16" />
                        <span>Loading your accounts</span>
                    </div>
                )}
                {showAccounts.value &&
                    accountsLoaded.value &&
                    filteredAccounts.length === 0 && (
                        <div className="text-center">
                            No previous accounts found. Use login button below.
                        </div>
                    )}
                {showAccounts.value && accountsLoaded.value && (
                    <div className="overflow-y-overlay flex max-h-[50vh] flex-col gap-y-1">
                        {filteredAccounts.map((account) => (
                            <button
                                key={account.sub}
                                type="button"
                                onClick={() => loginAs(account, continuePath)}
                                className={cn(
                                    "group flex min-w-[360px] max-w-full items-center justify-between rounded-lg",
                                    "border border-transparent p-4 outline-none transition duration-200",
                                    "hover:border-[#488CC6] hover:bg-[#194061] focus:border-[#488CC6] focus:bg-[#194061]",
                                )}
                            >
                                <div className="flex items-center">
                                    <div className="relative">
                                        <img
                                            src={account.picture}
                                            alt={account.name}
                                            width="46"
                                            height="46"
                                            className="min-h-[46px] min-w-[46px] rounded-full object-cover shadow-inner-bold"
                                        />
                                        {waitingAccount.value ===
                                            account.sub && (
                                            <div className="absolute -inset-1">
                                                <Loader
                                                    width="54"
                                                    height="54"
                                                    className="text-sky-500"
                                                />
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex w-full items-center justify-between py-2">
                                        <div
                                            className={cn(
                                                "flex w-full flex-col items-start pl-4",
                                                "transition-all duration-200 group-hover:pl-6 group-focus:pl-6",
                                            )}
                                        >
                                            <h2 className="text-lg">
                                                {account.name}
                                            </h2>
                                            <h3 className="text-sm">
                                                {account.code}@{account.domain}
                                            </h3>
                                        </div>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    title="Remove account"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        account.remove();
                                    }}
                                    className={cn(
                                        "flex items-center justify-center rounded-full p-1 transition duration-200",
                                        "scale-90 opacity-0 group-hover:scale-100 group-hover:opacity-100",
                                        "outline-none focus:scale-100 focus:text-red-500 focus:opacity-100",
                                        "text-white hover:text-red-500",
                                    )}
                                >
                                    <DeleteIcon
                                        width="24"
                                        height="24"
                                        className="fill-current"
                                    />
                                </button>
                            </button>
                        ))}
                    </div>
                )}
                <div className="mt-12 min-h-[56px]">
                    {connecting.value || waiting.value ? (
                        <div className="flex min-h-[56px] items-center gap-x-2">
                            <Loader width="32" height="32" />
                            <span>Authorizing</span>
                        </div>
                    ) : connection.value ? (
                        <span>Logged in</span>
                    ) : (
                        <button
                            type="button"
                            onClick={handleLogin}
                            className={cn(
                                "flex cursor-pointer items-center rounded-full bg-[#1B5281] px-6 py-1 text-xl font-bold transition duration-200",
                                "hover:bg-[#194061] focus:bg-[#194061] focus:outline focus:outline-4 focus:outline-sky-500",
                            )}
                        >
                            <span className="mr-2">
                                {showAccounts.value &&
                                filteredAccounts.length > 0
                                    ? "Login with different account"
                                    : "Login"}
                            </span>
                            <ChevronIcon
                                width="32"
                                height="32"
                                className="fill-white"
                            />
                        </button>
                    )}
                </div>
            </div>
            <div className="absolute bottom-2 ios:bottom-8 left-1/2 -translate-x-1/2 md:bottom-6 md:left-auto md:right-6 md:translate-x-0">
                <BottomIcon className="max-w-[60vw]" />
            </div>
        </div>
    );
};

export default Login;
